import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { ref, get, push, set } from 'firebase/database';
import { database } from '../../../../firebase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000";

const REVIEW_TYPES = [
  'annual',
  'quarterly',
  'mid_year',
  'probation',
  'performance',
  'one_on_one',
  'other'
];

const REVIEW_TYPE_LABELS = {
  'annual': 'Annual',
  'quarterly': 'Quarterly',
  'mid_year': 'Mid Year',
  'probation': 'Probation',
  'performance': 'Performance',
  'one_on_one': 'One on One',
  'other': 'Other'
};

// Dark theme styles
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3000,
  },
  modal: {
    background: '#0B1C34',
    border: '1px solid rgba(86, 141, 237, 0.2)',
    borderRadius: '12px',
    width: '600px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflow: 'auto',
    color: '#FFFFFF',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(86, 141, 237, 0.15)',
  },
  title: {
    margin: 0,
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '20px',
    fontWeight: 600,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '24px',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
  },
  body: {
    padding: '24px',
  },
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    marginBottom: 8,
    fontSize: '14px',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '16px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '16px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    minHeight: '100px',
    padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  readOnly: {
    padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '8px',
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '16px',
  },
  statusBox: {
    marginTop: '20px',
    padding: '12px 16px',
    borderRadius: '8px',
    fontFamily: 'Montserrat',
    fontSize: '14px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '20px 24px',
    borderTop: '1px solid rgba(86, 141, 237, 0.15)',
  },
  cancelBtn: {
    padding: '10px 20px',
    background: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 20px',
    background: '#568ded',
    border: 'none',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

const ScheduleReviewModal = ({ employeeId, companyUID, employeeName, employeeEmail, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const [reviewType, setReviewType] = useState('performance');
  const [reviewDate, setReviewDate] = useState('');
  const [reviewTime, setReviewTime] = useState('09:00');
  const [notes, setNotes] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [scheduleStatus, setScheduleStatus] = useState('');

  // Load calendar connections from Firebase
  useEffect(() => {
    const loadCalendarConnections = async () => {
      try {
        const calendarRef = ref(database, `companies/${companyUID}/calendarIntegrations`);
        const snapshot = await get(calendarRef);
        
        if (snapshot.exists()) {
          const connections = Object.values(snapshot.val());
          setCalendarConnections(connections);
        } else {
          setCalendarConnections([]);
        }
      } catch (error) {
        console.error('Error loading calendar connections:', error);
        setCalendarConnections([]);
      }
    };
    
    if (companyUID) {
      loadCalendarConnections();
    }
  }, [companyUID]);

  const handleSchedule = async () => {
    if (!reviewDate) {
      alert('Please select a review date');
      return;
    }

    setScheduling(true);
    setScheduleStatus('📅 Scheduling review...');

    try {
      const reviewDateTime = `${reviewDate}T${reviewTime}:00`;
      const startDateTime = new Date(reviewDateTime);
      const endDateTime = new Date(startDateTime.getTime() + (60 * 60000)); // 60 minutes default

      // First, save the review to Firebase
      const response = await fetch(`${BACKEND_URL}/employee/schedule-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyUID,
          employeeId,
          reviewType,
          reviewDate: reviewDateTime,
          notes: notes.trim() || undefined,
          scheduledByUID: currentUser?.uid,
          scheduledByName: currentUser?.displayName || currentUser?.email || 'Manager'
        })
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to schedule review');
      }

      // If calendar is connected, create calendar event
      if (calendarConnections.length > 0) {
        const calendarConnection = calendarConnections.find(conn => 
          conn.provider === 'google' || conn.provider === 'outlook'
        );

        if (calendarConnection) {
          setScheduleStatus('📅 Creating calendar event...');
          
          const reviewTitle = `Performance Review - ${employeeName}`;
          const reviewDescription = `Performance Review: ${REVIEW_TYPE_LABELS[reviewType] || reviewType}\n\n${notes.trim() || 'No additional notes'}`;
          
          // Get employee email for attendee list
          const attendeeEmails = employeeEmail ? [employeeEmail] : [];

          const calendarRequest = {
            company_uid: companyUID,
            employee_id: employeeId,
            review_title: reviewTitle,
            duration_minutes: 60,
            employee_email: employeeEmail,
            employee_name: employeeName,
            start_time: startDateTime.toISOString(),
            end_time: endDateTime.toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            attendee_emails: attendeeEmails,
            description: reviewDescription,
            token_data: {
              provider: calendarConnection.provider,
              access_token: calendarConnection.access_token,
              refresh_token: calendarConnection.refresh_token,
              token_uri: calendarConnection.token_uri,
              client_id: calendarConnection.client_id,
              client_secret: calendarConnection.client_secret,
              scopes: calendarConnection.scopes
            }
          };

          const calendarResponse = await fetch(`${BACKEND_URL}/schedule-review-calendar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(calendarRequest)
          });

          const calendarResult = await calendarResponse.json();
          if (calendarResult.success) {
            setScheduleStatus('✅ Review scheduled with calendar invite!');
          } else {
            setScheduleStatus('✅ Review scheduled (calendar event failed)');
          }
        } else {
          setScheduleStatus('✅ Review scheduled successfully!');
        }
      } else {
        setScheduleStatus('✅ Review scheduled successfully!');
      }

      // Send Notification (Firebase)
      const notifTitle = 'Performance Review Scheduled';
      const notifMessage = `A ${REVIEW_TYPE_LABELS[reviewType] || reviewType} review has been scheduled for ${new Date(reviewDateTime).toLocaleDateString()} at ${reviewTime}.`;
      
      try {
        const notifRef = push(ref(database, `companies/${companyUID}/employees/${employeeId}/notifications`));
        await set(notifRef, {
          title: notifTitle,
          message: notifMessage,
          type: 'info',
          timestamp: Date.now(),
          read: false
        });
      } catch (err) { console.error(err); }

      // Send Notification (Email)
      try {
        await fetch(`${BACKEND_URL}/send-notification-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyId: companyUID,
            employeeId,
            subject: notifTitle,
            message: notifMessage,
            type: 'info'
          })
        });
      } catch (err) { console.error('Email notification failed:', err); }

      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1000);

    } catch (error) {
      console.error('Schedule review failed:', error);
      setScheduleStatus('❌ Failed to schedule review');
      alert('Failed to schedule review: ' + (error.message || 'Unknown error'));
      setScheduling(false);
    }
  };

  // Set default date to tomorrow
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setReviewDate(tomorrow.toISOString().split('T')[0]);
  }, []);

  const isSuccess = scheduleStatus.includes('✅');
  const isError = scheduleStatus.includes('❌');

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Schedule Performance Review</h2>
          <button style={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>
        <div style={styles.body}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Employee</label>
            <div style={styles.readOnly}>
              {employeeName || 'Unknown Employee'}
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Review Type</label>
            <select
              value={reviewType}
              onChange={(e) => setReviewType(e.target.value)}
              style={styles.select}
            >
              {REVIEW_TYPES.map(type => (
                <option key={type} value={type} style={{ background: '#0B1C34', color: '#FFFFFF' }}>
                  {REVIEW_TYPE_LABELS[type] || type}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                Date <span style={{ color: '#EB4040' }}>*</span>
              </label>
              <input
                type="date"
                value={reviewDate}
                onChange={(e) => setReviewDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                style={styles.input}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Time</label>
              <input
                type="time"
                value={reviewTime}
                onChange={(e) => setReviewTime(e.target.value)}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this review..."
              style={styles.textarea}
            />
          </div>
          
          {/* Schedule Status */}
          {scheduleStatus && (
            <div style={{
              ...styles.statusBox,
              backgroundColor: isError ? 'rgba(235, 64, 64, 0.15)' : 'rgba(64, 235, 110, 0.15)',
              border: `1px solid ${isError ? 'rgba(235, 64, 64, 0.3)' : 'rgba(64, 235, 110, 0.3)'}`,
              color: isError ? '#EB4040' : '#40EB6E',
            }}>
              {scheduleStatus}
            </div>
          )}
        </div>
        {!isSuccess && (
          <div style={styles.footer}>
            <button
              onClick={onClose}
              disabled={scheduling}
              style={{
                ...styles.cancelBtn,
                opacity: scheduling ? 0.5 : 1,
                cursor: scheduling ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSchedule}
              disabled={scheduling || !reviewDate}
              style={{
                ...styles.submitBtn,
                background: scheduling || !reviewDate ? 'rgba(86, 141, 237, 0.4)' : '#568ded',
                cursor: scheduling || !reviewDate ? 'not-allowed' : 'pointer',
              }}
            >
              {scheduling ? 'Scheduling...' : 'Schedule Review'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleReviewModal;
